import { z } from 'zod';

import { ERP_NAMES } from '@/domain/companies/helpers';

// The backend returns null for a field that was never set, and omits it in some
// responses — both collapse to the empty string the form needs. `.default()`
// alone would not do it: it only fires on undefined.
const FTPConnectionFieldSchema = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

// GET preferences/company/:id/ftp_connection — seeds the FTP dialog. The shape is
// inferred from its only consumer, not from a published contract, so a missing or
// null field degrades to '' while a wrong type reports drift.
export const FTPConnectionSchema = z.object({
  address: FTPConnectionFieldSchema,
  login: FTPConnectionFieldSchema,
  password: FTPConnectionFieldSchema,
});

export type FTPConnection = z.infer<typeof FTPConnectionSchema>;

// What we send is always three strings — the leniency above is for reading only.
export const FTPConnectionBodySchema = z.object({
  address: z.string(),
  login: z.string(),
  password: z.string(),
});

// Describes a successful save only. A failed connection answers 400 with `ftp_connection: null`, and the client
// rejects any non-2xx before this schema runs, so that body reaches `onError`, never here. Making the field
// nullable would describe a response this endpoint does not send.
export const FTPConnectionSaveResponseSchema = z.object({
  message: FTPConnectionFieldSchema,
  ftp_connection: z.string(),
});

export const ErpCompanyContactSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});

// Declared in full though only id and name are read: the response is the
// contract, and narrowing it would hide the rest from anyone reading this file.
export const ErpCompanySchema = z.object({
  organization_id: z.string(),
  id: z.string(),
  name: z.string(),
  picture: z.string(),
  contact: ErpCompanyContactSchema,
  country: z.string().nullable(),
  city: z.string().nullable(),
  street: z.string().nullable(),
  registration_number: z.string().nullable(),
  vat_number: z.string().nullable(),
  currency: z.string().nullable(),
  email_box: z.string(),
  confidential_email_box: z.string().nullable(),
  is_connected_to_erp: z.boolean(),
  whatsapp_phone: z.string().nullable(),
  amounts_representation: z.string(),
  preparation_days: z.number(),
});

export const ErpCompaniesSchema = z.array(ErpCompanySchema);

export type ErpCompany = z.infer<typeof ErpCompanySchema>;

// Omitted, not empty: the endpoint then answers for the signed-in company's own
// ERP, which no value on hand reproduces.
export const ErpCompaniesQuerySchema = z.object({ erpType: z.string().optional() });

export const ActivateErpQuerySchema = z.object({ erpType: z.enum([ERP_NAMES.defaultErp, ERP_NAMES.qbd]) });

export const ErpSSOParamsQuerySchema = z.object({
  erpType: z.enum([ERP_NAMES.xero, ERP_NAMES.qbo, ERP_NAMES.sage, ERP_NAMES.zoho]),
});

export const ErpSSOParamsSchema = z.object({
  endpoint: z.string(),
  params: z.record(z.string(), z.string()),
});

export const HashDeviceSchema = z.object({
  deviceId: z.string(),
  displayGuid: z.string(),
  deviceType: z.string(),
  erpType: z.string(),
});

export const HashDevicesSchema = z.array(HashDeviceSchema);

export type HashDevice = z.infer<typeof HashDeviceSchema>;

export const ZohoOrganizationSchema = z.object({
  organization_id: z.string(),
  organization_name: z.string(),
});

export const ZohoOrganizationsSchema = z.object({
  organizations: z.array(ZohoOrganizationSchema),
});

// Present on a 'suspended' connection: the company stays connected, only background sync is paused.
export const ErpSyncSuspensionSchema = z.object({
  reason: z.string().optional(),
  reason_code: z.string().optional(),
  suspended_at: z.string().optional(),
});

export const ErpConnectionSchema = z.object({
  status: z.string(),
  // Per-ERP preferences whose other keys are not knowable here, so they are kept, not stripped.
  details: z.looseObject({
    company_name: z.string().optional(),
    sync_suspension: ErpSyncSuspensionSchema.optional(),
  }),
});

export const ErpConnectionsSchema = z.record(z.string(), ErpConnectionSchema);

export type ErpSyncSuspension = z.infer<typeof ErpSyncSuspensionSchema>;
export type ErpConnection = z.infer<typeof ErpConnectionSchema>;
export type ErpConnections = z.infer<typeof ErpConnectionsSchema>;

// Sign-in step of the multi-step ERP connection dialog (Sage, Priority, MS Dynamics, SAP 4 HANA).
// Only the fields the dialog reads; `versions` / `defaultVersion` are still returned for Priority
// but deliberately not read (DA-20083).
export const ErpSignInCompanySchema = z.object({
  ID: z.union([z.string(), z.number()]),
  Name: z.string(),
});

// A refused sign-in answers `{ error: true, message, companies: null, … }`, so every field is nullish.
export const ErpSignInResponseSchema = z.object({
  error: z.boolean().nullish(),
  message: z.string().nullish(),
  companies: z.array(ErpSignInCompanySchema).nullish(),
  selected: z.union([z.string(), z.number()]).nullish(),
  // Opaque: handed back unchanged in the company-link request.
  erpToken: z.unknown(),
});

export type ErpSignInResponse = z.infer<typeof ErpSignInResponseSchema>;

export const ZohoSaveOrganizationBodySchema = z.object({ organization_id: z.string() });

// The body is a bare "OK" — declared so drift is visible, not because anything reads it.
export const SyncErpIndexesResponseSchema = z.string();
